import { useState, useRef, useCallback } from 'react';
import { DEFAULT_SPEECH_STYLE, SpeechBubbleStyle } from '../utils/speechStyles';

export interface SpeechEntry {
  text: string;
  style: SpeechBubbleStyle;
  /** Timestamp of this message — used as React key to restart CSS animation. */
  ts: number;
}

export function useSpeechBubbles() {
  const [speeches, setSpeeches] = useState<Map<number, SpeechEntry>>(new Map());
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  /** Call when a "said" WS event or history event arrives. */
  const handleSaidEvent = useCallback((pointId: number, text: string, style?: SpeechBubbleStyle) => {
    const existing = timersRef.current.get(pointId);
    if (existing) clearTimeout(existing);

    const ts = Date.now();
    setSpeeches(prev => {
      const next = new Map(prev);
      next.set(pointId, { text, style: style ?? DEFAULT_SPEECH_STYLE, ts });
      return next;
    });

    const timer = setTimeout(() => {
      setSpeeches(prev => {
        const next = new Map(prev);
        next.delete(pointId);
        return next;
      });
      timersRef.current.delete(pointId);
    }, 5000);
    timersRef.current.set(pointId, timer);
  }, []);

  /** Clear all active bubbles — call on scrub / page leave. */
  const clearSpeeches = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current.clear();
    setSpeeches(new Map());
  }, []);

  return { speeches, handleSaidEvent, clearSpeeches, timersRef };
}
